/* ============================================
   AudioRecorder — Record voice messages
   Uses MediaRecorder API with webm output
   States: idle → recording → paused
   ============================================ */
import { useState, useRef, useEffect, useCallback } from 'react'
import { IconTrash, IconPause, IconPlay, IconSend, IconAlertCircle } from '@/components/icons/Icons'

interface AudioRecorderProps {
  onSend: (blob: Blob, duration: number) => void
  onCancel: () => void
}

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export default function AudioRecorder({ onSend, onCancel }: AudioRecorderProps) {
  const [state, setState] = useState<'requesting' | 'recording' | 'paused' | 'error'>('requesting')
  const [elapsed, setElapsed] = useState(0)
  const [errorMsg, setErrorMsg] = useState('')
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef(0)
  const pausedTimeRef = useRef(0)

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now() - pausedTimeRef.current * 1000
    timerRef.current = setInterval(() => {
      setElapsed((Date.now() - startTimeRef.current) / 1000)
    }, 100)
  }, [])

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    let stream: MediaStream | null = null

    async function init() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm'
        const recorder = new MediaRecorder(stream, { mimeType })

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data)
        }

        recorderRef.current = recorder
        chunksRef.current = []
        recorder.start(250)
        setState('recording')
        startTimer()
      } catch {
        setErrorMsg('No se pudo acceder al micrófono. Verifica los permisos del navegador.')
        setState('error')
      }
    }

    init()

    return () => {
      stopTimer()
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop()
      }
      if (stream) {
        stream.getTracks().forEach(t => t.stop())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePauseResume = () => {
    const recorder = recorderRef.current
    if (!recorder) return

    if (state === 'recording') {
      recorder.pause()
      pausedTimeRef.current = elapsed
      stopTimer()
      setState('paused')
    } else if (state === 'paused') {
      recorder.resume()
      startTimer()
      setState('recording')
    }
  }

  const handleCancel = () => {
    stopTimer()
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
    }
    if (recorder?.stream) {
      recorder.stream.getTracks().forEach(t => t.stop())
    }
    onCancel()
  }

  const handleSend = () => {
    const recorder = recorderRef.current
    if (!recorder) return

    stopTimer()

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType })
      const durationSec = Math.round(elapsed)
      recorder.stream.getTracks().forEach(t => t.stop())
      onSend(blob, durationSec)
    }

    if (recorder.state !== 'inactive') {
      recorder.stop()
    }
  }

  if (state === 'error') {
    return (
      <div className="chat-recorder">
        <IconAlertCircle size={18} style={{ color: 'var(--danger)' }} />
        <span style={{ flex: 1, fontSize: '0.82rem', color: 'var(--danger)' }}>{errorMsg}</span>
        <button className="chat-recorder__btn" onClick={onCancel} type="button">
          Cerrar
        </button>
      </div>
    )
  }

  if (state === 'requesting') {
    return (
      <div className="chat-recorder">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <div className="auth-spinner" style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Solicitando micrófono...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="chat-recorder">
      {/* Cancel / trash */}
      <button
        className="chat-recorder__cancel"
        onClick={handleCancel}
        type="button"
        title="Cancelar"
      >
        <IconTrash size={18} />
      </button>

      {/* Timer + recording indicator */}
      <div className="chat-recorder__indicator">
        <span className={`chat-recorder__dot ${state === 'paused' ? 'chat-recorder__dot--paused' : ''}`} />
        <span className="chat-recorder__timer">{formatTimer(elapsed)}</span>

        {/* Mini waveform bars */}
        <div className="chat-recorder__waveform">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="chat-recorder__waveform-bar"
              style={{
                height: state === 'recording'
                  ? `${4 + Math.sin(Date.now() / 200 + i) * 10 + Math.random() * 6}px`
                  : '4px',
                transition: 'height 0.1s ease',
              }}
            />
          ))}
        </div>
      </div>

      {/* Pause / Resume */}
      <div className="chat-recorder__actions">
        <button
          className="chat-recorder__btn"
          onClick={handlePauseResume}
          type="button"
          title={state === 'recording' ? 'Pausar' : 'Reanudar'}
        >
          {state === 'recording' ? <IconPause size={18} /> : <IconPlay size={18} />}
        </button>

        {/* Send */}
        <button
          className="chat-recorder__btn chat-recorder__btn--send"
          onClick={handleSend}
          type="button"
          title="Enviar"
        >
          <IconSend size={18} />
        </button>
      </div>
    </div>
  )
}
